import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Tag, Button, Spin, Select } from "antd";
import {
  ExperimentOutlined,
  ExportOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { testingAnalyticsSummarySaga } from "./saga";
import * as selectors from "./selectors";

const { Title, Text } = Typography;

const TestingAnalyticsSummary = ({
  filters = {},
  onDetailedClick = () => {},
}) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: testingAnalyticsSummarySaga });

  const dispatch = useDispatch();
  const [sortBy, setSortBy] = useState("lowest");

  const loading = useSelector(selectors.selectLoading);
  const data = useSelector(selectors.selectSummaryData);
  const error = useSelector(selectors.selectError);

  const filterString = JSON.stringify(filters);

  useEffect(() => {
    dispatch(actions.fetchSummary({ filtersString: filterString, sortBy }));
  }, [filterString, sortBy, dispatch]);

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  if (loading) {
    return (
      <Card
        title={
          <span style={{ color: "#fff" }}>
            <ExperimentOutlined style={{ marginRight: 8 }} />
            Testing Analytics
          </span>
        }
        headStyle={{ backgroundColor: "#0D9488" }}
      >
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  // Show error state if there's an API error
  if (error && !data) {
    return (
      <Card
        title={
          <span style={{ color: "#fff" }}>
            <ExperimentOutlined style={{ marginRight: 8 }} />
            Testing Analytics
          </span>
        }
        headStyle={{ backgroundColor: "#0D9488" }}
      >
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Text type="danger">Error loading data: {error.message}</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <span style={{ color: "#fff", fontSize: "16px" }}>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          Testing Analytics
        </span>
      }
      extra={
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button
            type="default"
            icon={
              sortBy === "highest" ? (
                <SortDescendingOutlined />
              ) : (
                <SortAscendingOutlined />
              )
            }
            onClick={() =>
              setSortBy(sortBy === "highest" ? "lowest" : "highest")
            }
          >
            {sortBy === "highest" ? "High TAT" : "Low TAT"}
          </Button>
          <Button onClick={onDetailedClick} icon={<ExportOutlined />}>
            Detailed View
          </Button>
        </div>
      }
      headStyle={{ backgroundColor: "#0D9488" }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text strong>Test Completion Funnel</Text>
          <ul style={{ listStyle: "none", paddingLeft: 0, marginTop: 10 }}>
            <li>
              <Tag color="blue" /> Received at Lab:{" "}
              <strong>{data?.receivedAtLab || 0}</strong>
            </li>
            <li>
              <Tag color="gold" /> Under Testing:{" "}
              <strong>{data?.underTesting || 0}</strong>
            </li>
            <li>
              <Tag color="green" /> Report Uploaded:{" "}
              <strong>{data?.reportUploaded || 0}</strong>
            </li>
            <li>
              <Tag color="gray" /> Completed:{" "}
              <strong>{data?.completed || 0}</strong>
            </li>
            <li>
              <Tag color="red" /> Under Dispute:{" "}
              <strong>{data?.underDispute || 0}</strong>
            </li>
          </ul>
        </Col>
        <Col span={12}>
          <Card
            bordered={false}
            style={{ backgroundColor: "#f9f5ff", textAlign: "center" }}
          >
            <Text>Total Tested</Text>
            <Title level={3} style={{ color: "#8e44ad" }}>
              {data?.totalOrderTested || 0}
            </Title>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            bordered={false}
            style={{ backgroundColor: "#fffbea", textAlign: "center" }}
          >
            <Text>Avg Testing TAT</Text>
            <Title level={3} style={{ color: "#f39c12" }}>
              {data?.avgTat || 0} days
            </Title>
          </Card>
        </Col>
        <Col span={24}>
          <Title level={5}>Lab Performance Overview</Title>
          {Array.isArray(data?.labData) &&
            data.labData.map((lab, index) => (
              <Card key={index} style={{ marginBottom: 12 }}>
                <Row justify="space-between" align="middle">
                  <Col span={10}>
                    <Text strong>{lab.labName}</Text>
                    <br />
                    <Text type="secondary">
                      Samples Tested: {lab.sampleTested}
                    </Text>
                  </Col>
                  <Col span={6} style={{ textAlign: "center" }}>
                    <Text strong>Avg TAT</Text>
                    <br />
                    <Text>{lab.avgTat} days</Text>
                  </Col>
                  <Col span={6} style={{ textAlign: "center" }}>
                    <Tag color={lab.pending > 30 ? "red" : "blue"}>
                      {lab.pending} pending
                    </Tag>
                  </Col>
                </Row>
              </Card>
            ))}
        </Col>
      </Row>
    </Card>
  );
};

export default TestingAnalyticsSummary;
